import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Send, ArrowLeft, Circle, Loader2, Search, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const MessagesPage = () => {
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(searchParams.get('chat') || null);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [otherOnline, setOtherOnline] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [userId, setUserId] = useState(null);

  // Get current user ID
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setUserId(r.data?.id || r.data?.user?.id)).catch(() => {});
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data.conversations || []);
      const unread = await axios.get(`${API}/messages/unread-count`);
      setUnreadCount(unread.data.unread_count || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Fetch chat messages
  const fetchMessages = useCallback(async (otherUserId) => {
    try {
      const res = await axios.get(`${API}/messages/conversation/${otherUserId}`);
      setMessages(res.data.messages || []);
      setOtherUser(res.data.other_user);
      setOtherOnline(res.data.other_online);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (activeChat) fetchMessages(activeChat);
  }, [activeChat, fetchMessages]);

  // WebSocket connection
  useEffect(() => {
    if (!userId || !WS_URL) return;
    try {
      const ws = new WebSocket(`${WS_URL}/api/messages/ws/${userId}`);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === 'new_message') {
          const msg = data.message;
          if (msg.sender_id === activeChat || msg.recipient_id === activeChat) {
            setMessages(prev => [...prev, msg]);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          }
          fetchConversations();
        }
      };
      ws.onerror = () => {};
      wsRef.current = ws;
      return () => ws.close();
    } catch (e) { /* WS not available */ }
  }, [userId, activeChat, fetchConversations]);

  const handleSend = async () => {
    if (!text.trim() || !activeChat) return;
    setSending(true);
    try {
      // Try WebSocket first
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', recipient_id: activeChat, text: text.trim() }));
      } else {
        await axios.post(`${API}/messages/send`, { recipient_id: activeChat, text: text.trim() });
      }
      // Optimistic update
      setMessages(prev => [...prev, { id: Date.now().toString(), sender_id: userId, text: text.trim(), created_at: new Date().toISOString() }]);
      setText('');
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      fetchConversations();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const openChat = (otherUserId) => { setActiveChat(otherUserId); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>;

  return (
    <div className="f500-page max-w-4xl mx-auto" data-testid="messages-page">
      <div className="flex gap-0 h-[calc(100vh-120px)]  overflow-hidden border border-[var(--isp-glass-border)] bg-[var(--isp-glass)]">
        {/* Sidebar */}
        <div className={`w-80 border-r border-[var(--isp-glass-border)] flex flex-col ${activeChat ? 'hidden md:flex' : 'flex w-full md:w-80'}`} data-testid="conversations-list">
          <div className="p-4 border-b border-[var(--isp-glass-border)]">
            <h2 className="text-lg font-bold text-[var(--isp-heading)] flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" /> Messages
              {unreadCount > 0 && <span className="px-2 py-0.5 rounded-full bg-purple-500 text-white text-xs">{unreadCount}</span>}
            </h2>
            <div className="mt-2 relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--isp-muted)]" />
              <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search..." className="h-8 pl-8 text-xs bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]" data-testid="msg-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-[var(--isp-muted)]" />
                <p className="text-xs text-[var(--isp-muted)]">No conversations yet</p>
                <p className="text-[10px] text-[var(--isp-muted)] mt-1">Follow students from the Feed to start chatting</p>
              </div>
            ) : conversations.filter(c => !searchQ || c.other_user?.name?.toLowerCase().includes(searchQ.toLowerCase())).map(c => (
              <button key={c.id} onClick={() => openChat(c.other_user?.id)}
                className={`w-full p-3 flex items-center gap-3 text-left border-b border-[var(--isp-glass-border)] transition-colors hover:bg-white/[0.04] ${activeChat === c.other_user?.id ? 'bg-purple-500/10' : ''}`}
                data-testid={`convo-${c.other_user?.id}`}>
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                    {(c.other_user?.name || 'S')[0].toUpperCase()}
                  </div>
                  {c.other_online && <Circle className="w-3 h-3 fill-green-400 text-green-400 absolute -bottom-0.5 -right-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{c.other_user?.name}</p>
                  <p className="text-[10px] text-[var(--isp-muted)] truncate">{c.last_message}</p>
                </div>
                {c.unread_count > 0 && <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center">{c.unread_count}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!activeChat ? 'hidden md:flex' : 'flex'}`} data-testid="chat-area">
          {!activeChat ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-[var(--isp-muted)]" />
                <p className="text-sm text-[var(--isp-muted)]">Select a conversation to start chatting</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="p-3 border-b border-[var(--isp-glass-border)] flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setActiveChat(null)} className="md:hidden p-1"><ArrowLeft className="w-4 h-4" /></Button>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                  {(otherUser?.name || 'S')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--isp-heading)]">{otherUser?.name}</p>
                  <p className={`text-[10px] ${otherOnline ? 'text-green-400' : 'text-[var(--isp-muted)]'}`}>{otherOnline ? 'Online' : 'Offline'}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {messages.map((m, i) => {
                  const isMine = m.sender_id === userId;
                  return (
                    <motion.div key={m.id || i} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-3 py-2  text-sm ${isMine ? 'bg-purple-500/20 text-[var(--isp-heading)] rounded-br-sm' : 'bg-white/[0.06] text-[var(--isp-body)] rounded-bl-sm'}`}>
                        <p>{m.text}</p>
                        <p className="text-[9px] text-[var(--isp-muted)] mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </motion.div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-[var(--isp-glass-border)] flex gap-2">
                <Input value={text} onChange={e => setText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Type a message..." className="flex-1 bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
                  data-testid="msg-input" />
                <Button onClick={handleSend} disabled={!text.trim() || sending}
                  className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="msg-send"
                  aria-label="Send message">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
